import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EllipsisVertical, Pencil } from 'lucide-react';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import { CONCEPT_ICONS } from '../utils/concept-icons';

const ShareIcon = CONCEPT_ICONS.share;
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import { getIconComponent } from './IconPicker';
import { useLocale } from '../contexts/LocaleContext';
import { handleModifierAwareNavigate } from '../utils/app-navigation';
import { cn } from '@/lib/utils';
import { CardMetaText } from './CardMetaText';
import type { DeskProject } from '../types/desk-project';
import { toBcp47Locale } from '../locales/locale';

interface DeskProjectCardProps {
  project: DeskProject;
  isTrash: boolean;
  onDelete: (projectId: string) => void;
  onRestore: (projectId: string) => void;
}

function formatDeskDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(toBcp47Locale(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

function projectDocumentPath(project: DeskProject, extra = ''): string | null {
  if (!project.mainDocumentId) {
    return null;
  }
  return `/projects/${project.id}/documents/${project.mainDocumentId}${extra}`;
}

interface ProjectShortcutButtonProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

const ProjectShortcutButton: React.FC<ProjectShortcutButtonProps> = ({
  href,
  children,
  className,
  style,
  title,
}) => {
  const navigate = useNavigate();

  return (
    <Button
      variant="outline"
      size="sm"
      className={className}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        handleModifierAwareNavigate(e, navigate, href);
      }}
      onAuxClick={(e) => {
        e.stopPropagation();
        handleModifierAwareNavigate(e, navigate, href);
      }}
      style={style}
    >
      {children}
    </Button>
  );
};

export const DeskProjectCard: React.FC<DeskProjectCardProps> = ({
  project,
  isTrash,
  onDelete,
  onRestore,
}) => {
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const documentPath = projectDocumentPath(project);
  const chaptersPath = projectDocumentPath(project, '/chapters');
  const sharingPath = projectDocumentPath(project, '?panel=sharing');
  const resourcesPath = `/projects/${project.id}/resources`;
  const MainIcon = project.mainDocumentIcon ? getIconComponent(project.mainDocumentIcon) : null;

  return (
    <Card
      className={cn(
        'relative border-0 shadow-none rounded-[10px]',
        documentPath && 'cursor-pointer transition-all hover:bg-card'
      )}
      style={{ border: '1px solid var(--border)' }}
      onClick={(e) => {
        if (!documentPath) {
          return;
        }
        handleModifierAwareNavigate(e, navigate, documentPath);
      }}
      onAuxClick={(e) => {
        if (!documentPath) {
          return;
        }
        handleModifierAwareNavigate(e, navigate, documentPath);
      }}
    >
      <div
        style={{
          padding: '1rem',
          paddingRight: '3rem',
          display: 'flex',
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textDecoration: 'none',
            color: 'inherit',
            display: 'flex',
            alignItems: 'stretch',
            gap: '0.75rem',
          }}
        >
          {MainIcon ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--foreground)',
                minWidth: '48px',
                maxWidth: '48px',
                flexShrink: 0,
                alignSelf: 'stretch',
                height: '100%',
              }}
              className="project-icon-container"
            >
              <MainIcon size={48} />
            </div>
          ) : null}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', lineHeight: '1.5', overflowWrap: 'anywhere' }}>
              {project.title}
            </h3>
            <CardMetaText>
              {formatDeskDate(project.createdAt, locale)} · {t('last_update')}: {formatDeskDate(project.updatedAt, locale)}
            </CardMetaText>
            {project.sharedWith?.length > 0 && (
              <CardMetaText
                className="flex min-w-0 items-center gap-1.5"
                title={project.sharedWith.join(', ')}
              >
                <ShareIcon className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {t('shared_with')}: {project.sharedWith.join(', ')}
                </span>
              </CardMetaText>
            )}
            {!isTrash && (
              <div className="mt-3 flex min-w-0 flex-wrap gap-2">
                {documentPath && (
                  <ProjectShortcutButton
                    href={documentPath}
                    className="max-w-full min-w-0 overflow-hidden"
                    style={{ justifyContent: 'flex-start', paddingLeft: '0.75rem', paddingRight: '0.75rem' }}
                    title={project.mainDocumentTitle || t('document_chapter_title')}
                  >
                    {MainIcon ? (
                      <span className="inline-flex shrink-0 items-center">
                        <MainIcon size={16} />
                      </span>
                    ) : null}
                    <span className="min-w-0 truncate">
                      {project.mainDocumentTitle || t('document_chapter_title')}
                    </span>
                  </ProjectShortcutButton>
                )}
                {chaptersPath && (
                  <ProjectShortcutButton href={chaptersPath}>
                    {t('chapter_outline')}
                  </ProjectShortcutButton>
                )}
                <ProjectShortcutButton href={resourcesPath}>
                  {t('Resources')}
                </ProjectShortcutButton>
                {sharingPath && (
                  <ProjectShortcutButton href={sharingPath}>
                    {t('share_document')}
                  </ProjectShortcutButton>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isTrash && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (documentPath) {
                  handleModifierAwareNavigate(e, navigate, documentPath);
                }
              }}
              onAuxClick={(e) => {
                e.stopPropagation();
                if (documentPath) {
                  handleModifierAwareNavigate(e, navigate, documentPath);
                }
              }}
            >
              <Pencil className="w-4 h-4" />
              {t('edit')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className={isTrash ? undefined : 'group'}
            onClick={(e) => {
              e.stopPropagation();
              if (isTrash) {
                onRestore(project.id);
              } else {
                onDelete(project.id);
              }
            }}
          >
            {isTrash ? (
              <>
                <span className="w-4 h-4" />
                {t('restore') || 'Restore'}
              </>
            ) : (
              <>
                <RotatingTrashIcon className="mr-2" />
                {t('move_to_trash')}
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Card>
  );
};
